import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormGroup } from '../FormGroup';

const Wrapper = ({ children, icon, addon, select, onAddonClick }) => {
  if (icon) {
    return (
      <div className={`has-icon-${icon.position}`}>
        {children}
        <i className={`form-icon icon ${icon.name}`} />
      </div>
    );
  }
  if (addon) {
    return (
      <div className="input-group">
        {children}
        <button type="button" className="input-group-addon addon-lg clear-btn c-hand" onClick={onAddonClick}>
          {addon}
        </button>
      </div>
    );
  }
  if (select) {
    return (
      <div className="input-group">
        {children}
        {select}
      </div>
    );
  }
  return <React.Fragment>{children}</React.Fragment>;
};

/**
 * An Input is a field used to elicit a response from a user.
 */
const Input = React.forwardRef((props, ref) => {
  const { className, label, id, icon, addon, select, error, onAddonClick, name, ...otherProps } = props;
  const formGroupProps = { label, id, error, name };
  const inputProps = { id, ref, name, ...otherProps };
  const classNames = classnames('form-input', className);
  return (
    <FormGroup {...formGroupProps}>
      <Wrapper icon={icon} addon={addon} onAddonClick={onAddonClick} select={select}>
        <input {...inputProps} className={classNames} />
      </Wrapper>
    </FormGroup>
  );
});

Input.propTypes = {
  /** Additional classes. */
  className: PropTypes.string,
  /** Optional Label to display along side the Input. */
  label: PropTypes.string,
  /** Input id */
  id: PropTypes.string,
  /** Additional element on the right side */
  addon: PropTypes.element,
  /** Click handler for addon */
  onAddonClick: PropTypes.func,
  /** AInput can have an Select on the right side */
  select: PropTypes.element,
  /** Input can have an icon */
  icon: PropTypes.shape({
    /** Icon class (see Icon component) */
    name: PropTypes.string,
    /** Icon position. One of 'left', 'right' */
    position: PropTypes.oneOf(['left', 'right']),
  }),
  /** Error message */
  error: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.element, PropTypes.string])),
  ]),
};

Input.defaultProps = {
  className: '',
  label: '',
  id: '',
  error: '',
  select: null,
  addon: null,
  onAddonClick: () => {},
  icon: null,
};

export { Input };
